// frontend/src/pages/GestionUsuarios.jsx

import React, { useState, useEffect } from 'react';
import usuariosService from '../services/usuariosService';
import authService from '../services/authService';
import { toast } from 'react-toastify';
import Loading from '../components/Loading';
import './GestionUsuarios.css';
import GestionUsuarioNuevoModal from './GestionUsuarioNuevoModal';
import GestionUsuarioEditarModal from './GestionUsuarioEditarModal';
import PushNotificationButton from '../components/PushNotificationButton';
import { PANTALLAS_SIDEBAR, TODAS_LAS_PANTALLAS, CLAVES_TABS_CONFIGURACION } from '../utils/pantallas';

const GestionUsuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [tiposUsuario, setTiposUsuario] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [showNuevoUsuarioModal, setShowNuevoUsuarioModal] = useState(false);
  const [showEditarUsuarioModal, setShowEditarUsuarioModal] = useState(false);
  const [showPermisosModal, setShowPermisosModal] = useState(false);
  const [showRolModal, setShowRolModal] = useState(false);
  const [showEtiquetasModal, setShowEtiquetasModal] = useState(false);
  const [showPantallasModal, setShowPantallasModal] = useState(false);
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [orden, setOrden] = useState({ campo: null, dir: 'asc' });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [etiquetasDisponibles, setEtiquetasDisponibles] = useState([]);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [puedeGestionar, setPuedeGestionar] = useState(false);
  const [guardandoEditorId, setGuardandoEditorId] = useState(null);

  const currentUser = authService.getUser();
  const isSuperuser = currentUser?.is_superuser === true;

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [usuariosData, tiposData, gruposData, etiquetasData] = await Promise.all([
        usuariosService.getUsuarios(),
        usuariosService.getTiposUsuario(),
        usuariosService.getGrupos(),
        usuariosService.getEtiquetasDisponibles().catch(() => ({ etiquetas: [] }))
      ]);

      setUsuarios(usuariosData.usuarios || []);
      setPuedeGestionar(usuariosData.puede_gestionar === true);
      setTiposUsuario(tiposData.tipos_usuario || []);
      setGrupos(gruposData.grupos || []);
      setEtiquetasDisponibles(etiquetasData.etiquetas || []);
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  // Obtener datos de la cuenta actual del usuario
  const getCuentaConfig = (usuario) => {
    return usuario.cuentas?.[0] || null;
  };

  // ¿El usuario tiene el grupo "Editores" en esta cuenta?
  const esEditor = (config) => !!config?.grupos?.some(g => g.name === 'Editores');

  // Toggle estado de usuario
  const handleToggleEstado = async (userId) => {
    const usuario = usuarios.find(u => u.id === userId);
    const accion = usuario.is_active ? 'desactivar' : 'activar';

    if (!window.confirm(`¿Estás seguro de ${accion} este usuario?`)) return;

    try {
      const response = await usuariosService.toggleEstado(userId);
      toast.success(response.message);
      setUsuarios(usuarios.map(u => u.id === userId ? response.usuario : u));
    } catch (error) {
      toast.error(error.error || 'Error al cambiar estado');
    }
  };

  // Switch "Editor predeterminado" (solo administradores)
  const handleToggleEditorPredeterminado = async (usuario) => {
    const config = getCuentaConfig(usuario);
    if (!config?.user_cuenta_id) return;
    setGuardandoEditorId(usuario.id);
    try {
      const response = await usuariosService.toggleEditorPredeterminado(
        config.user_cuenta_id,
        !config.editor_predeterminado
      );
      toast.success(response.message);
      setUsuarios(prev => prev.map(u => u.id === usuario.id ? response.usuario : u));
    } catch (error) {
      toast.error(error.error || 'Error al cambiar editor predeterminado');
    } finally {
      setGuardandoEditorId(null);
    }
  };

  // Pantallas del sidebar habilitadas por usuario
  const handleEditarPantallas = (usuario) => {
    setUsuarioSeleccionado(usuario);
    setShowPantallasModal(true);
  };

  const handleGuardarPantallas = async (userCuentaId, pantallas) => {
    try {
      const response = await usuariosService.actualizarPantallas(userCuentaId, pantallas);
      toast.success(response.message || 'Pantallas actualizadas');
      if (response.usuario) {
        setUsuarios(prev => prev.map(u => u.id === response.usuario.id ? response.usuario : u));
      }
      setShowPantallasModal(false);
      setUsuarioSeleccionado(null);
    } catch (error) {
      toast.error(error.error || 'Error al actualizar pantallas');
    }
  };

  // Editar usuario
  const handleEditarUsuario = (usuario) => {
    setUsuarioSeleccionado(usuario);
    setShowEditarUsuarioModal(true);
  };

  // Cambiar contraseña (solo superuser)
  const handleCambiarPassword = (usuario) => {
    setUsuarioSeleccionado(usuario);
    setShowPasswordModal(true);
  };

  const handleGuardarEdicionUsuario = async (userId, datosUsuario, userCuentaId) => {
    try {
      const response = await usuariosService.actualizarUsuario(userId, datosUsuario, userCuentaId);
      toast.success(response.message || 'Usuario actualizado');
      if (response.usuario) {
        setUsuarios(usuarios.map(u => u.id === userId ? response.usuario : u));
      } else {
        await cargarDatos();
      }
      setShowEditarUsuarioModal(false);
      setUsuarioSeleccionado(null);
    } catch (error) {
      throw error;
    }
  };

  // Crear usuario
  const handleCrearUsuario = async (datosUsuario) => {
    try {
      const response = await usuariosService.crearUsuario(datosUsuario);
      setUsuarios([...usuarios, response.usuario]);
      setShowNuevoUsuarioModal(false);
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Editar rol
  const handleEditarRol = (usuario) => {
    setUsuarioSeleccionado(usuario);
    setShowRolModal(true);
  };

  const handleActualizarRol = async (userId, tipoUsuarioId, userCuentaId) => {
    try {
      const response = await usuariosService.cambiarRol(userId, tipoUsuarioId, userCuentaId);
      toast.success(response.message || 'Rol actualizado');
      if (response.usuario) {
        setUsuarios(usuarios.map(u => u.id === userId ? response.usuario : u));
      } else {
        await cargarDatos();
      }
      setShowRolModal(false);
      setUsuarioSeleccionado(null);
    } catch (error) {
      toast.error(error.error || 'Error al actualizar rol');
    }
  };

  // Editar permisos/grupos
  const handleEditarPermisos = (usuario) => {
    setUsuarioSeleccionado(usuario);
    setShowPermisosModal(true);
  };

  const handleActualizarPermisos = async (userId, gruposIds, userCuentaId) => {
    try {
      const response = await usuariosService.actualizarGrupos(userId, gruposIds, userCuentaId);
      toast.success(response.message || 'Permisos actualizados');
      if (response.usuario) {
        setUsuarios(usuarios.map(u => u.id === userId ? response.usuario : u));
      } else {
        await cargarDatos();
      }
      setShowPermisosModal(false);
      setUsuarioSeleccionado(null);
    } catch (error) {
      toast.error(error.error || 'Error al actualizar permisos');
    }
  };

  // Editar etiquetas permitidas
  const handleEditarEtiquetas = (usuario) => {
    setUsuarioSeleccionado(usuario);
    setShowEtiquetasModal(true);
  };

  const handleActualizarEtiquetas = async (userId, etiquetas, userCuentaId) => {
    try {
      const response = await usuariosService.actualizarEtiquetas(userId, etiquetas, userCuentaId);
      toast.success(response.message || 'Etiquetas actualizadas');
      if (response.usuario) {
        setUsuarios(usuarios.map(u => u.id === userId ? response.usuario : u));
      } else {
        await cargarDatos();
      }
      setShowEtiquetasModal(false);
      setUsuarioSeleccionado(null);
    } catch (error) {
      toast.error(error.error || 'Error al actualizar etiquetas');
    }
  };

  // Valor de cada columna para ordenar
  const valorOrden = (usuario, campo) => {
    const config = getCuentaConfig(usuario);
    switch (campo) {
      case 'usuario': return (usuario.full_name || usuario.username || '').toLowerCase();
      case 'rol': return (config?.tipo_usuario?.nombre || '').toLowerCase();
      case 'grupos': return config?.grupos?.length || 0;
      case 'etiquetas': return usuario.etiquetas_permitidas?.length || 0;
      case 'pantallas': return Array.isArray(config?.pantallas_permitidas)
        ? config.pantallas_permitidas.filter(k => TODAS_LAS_PANTALLAS.includes(k)).length : 1;
      case 'editor': return config?.editor_predeterminado ? 1 : 0;
      case 'estado': return usuario.is_active ? 1 : 0;
      default: return '';
    }
  };

  const cambiarOrden = (campo) => {
    setOrden(prev => prev.campo === campo
      ? (prev.dir === 'asc' ? { campo, dir: 'desc' } : { campo: null, dir: 'asc' })
      : { campo, dir: 'asc' });
  };

  // Filtrar usuarios
  const usuariosFiltradosSinOrden = usuarios.filter(usuario => {
    if (!mostrarInactivos && !usuario.is_active) return false;
    if (!busqueda) return true;
    const searchLower = busqueda.toLowerCase();
    return (
      usuario.full_name?.toLowerCase().includes(searchLower) ||
      usuario.username?.toLowerCase().includes(searchLower) ||
      usuario.email?.toLowerCase().includes(searchLower)
    );
  });

  const usuariosFiltrados = orden.campo
    ? [...usuariosFiltradosSinOrden].sort((a, b) => {
        const va = valorOrden(a, orden.campo);
        const vb = valorOrden(b, orden.campo);
        const cmp = typeof va === 'string'
          ? va.localeCompare(vb, 'es', { numeric: true, sensitivity: 'base' })
          : va - vb;
        return orden.dir === 'asc' ? cmp : -cmp;
      })
    : usuariosFiltradosSinOrden;

  const Th = ({ campo, children }) => (
    <th
      className="th-sortable"
      onClick={() => cambiarOrden(campo)}
      aria-sort={orden.campo === campo ? (orden.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      {children}
      <span className={`sort-ind${orden.campo === campo ? ' active' : ''}`}>
        {orden.campo === campo ? (orden.dir === 'asc' ? '▲' : '▼') : '↕'}
      </span>
    </th>
  );

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="gestion-usuarios">
      <header className="page-header">
        <div className="header-content">
          <h1>Usuarios</h1>
          <span className="badge">{usuariosFiltrados.length}</span>
        </div>

        <div className="header-actions">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#6b7280', fontSize: '0.85rem' }}>
            <span>Notificaciones push</span>
            <PushNotificationButton />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#6b7280', fontSize: '0.85rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input
              type="checkbox"
              checked={mostrarInactivos}
              onChange={(e) => setMostrarInactivos(e.target.checked)}
            />
            Mostrar inactivos
          </label>
          <input
            type="text"
            placeholder="Buscar..."
            className="search-input"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
          <button
            className="il-btn-primary"
            onClick={() => setShowNuevoUsuarioModal(true)}
          >
            + Usuario
          </button>
        </div>
      </header>

      <div className="table-container">
        <table className="usuarios-table">
          <thead>
            <tr>
              <Th campo="usuario">Usuario</Th>
              <Th campo="rol">Rol</Th>
              <Th campo="grupos">Grupos</Th>
              <Th campo="etiquetas">Etiquetas</Th>
              <Th campo="pantallas">Pantallas</Th>
              <Th campo="editor">Editor</Th>
              <th aria-label="Acciones"></th>
            </tr>
          </thead>
          <tbody>
            {usuariosFiltrados.map(usuario => {
              const config = getCuentaConfig(usuario);
              return (
                <tr key={usuario.id}>
                  <td>
                    <div className="user-name">{usuario.full_name}</div>
                  </td>

                  <td>
                    <div className="cell-with-action">
                      {config?.tipo_usuario ? (
                        <span className="badge badge-primary">{config.tipo_usuario.nombre}</span>
                      ) : (
                        <span className="text-muted">Sin rol</span>
                      )}
                      <button className="btn-edit-small" onClick={() => handleEditarRol(usuario)} title="Editar rol">
                        ✎
                      </button>
                    </div>
                  </td>

                  <td>
                    <div className="cell-with-action">
                      {config?.grupos?.length > 0 ? (
                        <div className="badges-list">
                          <span className="badge badge-info">
                            {config.grupos.length} {config.grupos.length === 1 ? 'Asignado' : 'Asignados'}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted">Sin grupos</span>
                      )}
                      <button className="btn-edit-small" onClick={() => handleEditarPermisos(usuario)} title="Editar grupos">
                        ✎
                      </button>
                    </div>
                  </td>

                  <td>
                    <div className="cell-with-action">
                      {usuario.etiquetas_permitidas?.length > 0 ? (
                        <div className="badges-list">
                          {usuario.etiquetas_permitidas.slice(0, 2).map((et, i) => (
                            <span key={i} className="badge badge-etiqueta">🏷️ {et}</span>
                          ))}
                          {usuario.etiquetas_permitidas.length > 2 && (
                            <span className="badge badge-secondary">+{usuario.etiquetas_permitidas.length - 2}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted">Sin filtro</span>
                      )}
                      <button className="btn-edit-small" onClick={() => handleEditarEtiquetas(usuario)} title="Editar etiquetas">
                        ✎
                      </button>
                    </div>
                  </td>

                  <td>
                    <div className="cell-with-action">
                      {Array.isArray(config?.pantallas_permitidas) ? (
                        <span className="badge badge-info">
                          {config.pantallas_permitidas.filter(k => TODAS_LAS_PANTALLAS.includes(k)).length} de {TODAS_LAS_PANTALLAS.length}
                        </span>
                      ) : (
                        <span className="text-muted">Solo Calendario</span>
                      )}
                      {puedeGestionar && (
                        <button className="btn-edit-small" onClick={() => handleEditarPantallas(usuario)} title="Editar pantallas">
                          ✎
                        </button>
                      )}
                    </div>
                  </td>

                  <td>
                    <label
                      className={`gu-switch${(!puedeGestionar || !esEditor(config)) && !config?.editor_predeterminado ? ' is-disabled' : ''}`}
                      title={
                        !puedeGestionar
                          ? 'Solo administradores pueden cambiar esto'
                          : (!esEditor(config) && !config?.editor_predeterminado)
                            ? 'El usuario debe tener el grupo "Editores"'
                            : 'Asignar automáticamente a los posts nuevos'
                      }
                    >
                      <input
                        type="checkbox"
                        checked={!!config?.editor_predeterminado}
                        disabled={!puedeGestionar || !config || guardandoEditorId === usuario.id || (!esEditor(config) && !config.editor_predeterminado)}
                        onChange={() => handleToggleEditorPredeterminado(usuario)}
                      />
                      <span className="gu-switch-track" />
                    </label>
                  </td>

                  <td>
                    <div className="il-actions">
                      <button
                        className="il-btn-icon"
                        onClick={() => handleEditarUsuario(usuario)}
                        title="Editar usuario"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                      <button
                        className={`il-btn-icon${usuario.is_active ? ' danger' : ''}`}
                        onClick={() => handleToggleEstado(usuario.id)}
                        title={usuario.is_active ? 'Desactivar' : 'Activar'}
                      >
                        {usuario.is_active ? (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                          </svg>
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        )}
                      </button>
                      {isSuperuser && (
                        <button
                          className="il-btn-icon"
                          onClick={() => handleCambiarPassword(usuario)}
                          title="Cambiar contraseña"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {usuariosFiltrados.length === 0 && (
          <div className="empty-state">No se encontraron usuarios</div>
        )}
      </div>

      {/* Modal de nuevo usuario */}
      {showNuevoUsuarioModal && (
        <GestionUsuarioNuevoModal
          grupos={grupos}
          tiposUsuario={tiposUsuario}
          onClose={() => setShowNuevoUsuarioModal(false)}
          onSave={handleCrearUsuario}
        />
      )}

      {/* Modal de editar usuario */}
      {showEditarUsuarioModal && usuarioSeleccionado && (
        <GestionUsuarioEditarModal
          usuario={usuarioSeleccionado}
          cuenta={getCuentaConfig(usuarioSeleccionado)}
          onClose={() => {
            setShowEditarUsuarioModal(false);
            setUsuarioSeleccionado(null);
          }}
          onSave={handleGuardarEdicionUsuario}
        />
      )}

      {/* Modal de rol */}
      {showRolModal && usuarioSeleccionado && (
        <RolModal
          usuario={usuarioSeleccionado}
          cuenta={getCuentaConfig(usuarioSeleccionado)}
          tiposUsuario={tiposUsuario}
          onClose={() => {
            setShowRolModal(false);
            setUsuarioSeleccionado(null);
          }}
          onSave={handleActualizarRol}
        />
      )}

      {/* Modal de permisos */}
      {showPermisosModal && usuarioSeleccionado && (
        <PermisosModal
          usuario={usuarioSeleccionado}
          cuenta={getCuentaConfig(usuarioSeleccionado)}
          grupos={grupos}
          onClose={() => {
            setShowPermisosModal(false);
            setUsuarioSeleccionado(null);
          }}
          onSave={handleActualizarPermisos}
        />
      )}

      {/* Modal de etiquetas */}
      {showEtiquetasModal && usuarioSeleccionado && (
        <EtiquetasModal
          usuario={usuarioSeleccionado}
          cuenta={getCuentaConfig(usuarioSeleccionado)}
          etiquetasDisponibles={etiquetasDisponibles}
          onClose={() => {
            setShowEtiquetasModal(false);
            setUsuarioSeleccionado(null);
          }}
          onSave={handleActualizarEtiquetas}
        />
      )}

      {/* Modal de pantallas del sidebar */}
      {showPantallasModal && usuarioSeleccionado && (
        <PantallasModal
          usuario={usuarioSeleccionado}
          cuenta={getCuentaConfig(usuarioSeleccionado)}
          onClose={() => {
            setShowPantallasModal(false);
            setUsuarioSeleccionado(null);
          }}
          onSave={handleGuardarPantallas}
        />
      )}

      {/* Modal cambiar contraseña (solo superuser) */}
      {showPasswordModal && usuarioSeleccionado && (
        <CambiarPasswordModal
          usuario={usuarioSeleccionado}
          onClose={() => {
            setShowPasswordModal(false);
            setUsuarioSeleccionado(null);
          }}
        />
      )}
    </div>
  );
};

// Modal para habilitar pantallas del sidebar
const PantallasModal = ({ usuario, cuenta, onClose, onSave }) => {
  const actuales = cuenta?.pantallas_permitidas;
  const [personalizado, setPersonalizado] = useState(Array.isArray(actuales));
  const [seleccionadas, setSeleccionadas] = useState(Array.isArray(actuales) ? [...actuales] : [...TODAS_LAS_PANTALLAS]);
  const [saving, setSaving] = useState(false);

  const toggle = (key) => setSeleccionadas(prev => {
    const quitar = prev.includes(key);
    let next = quitar ? prev.filter(k => k !== key) : [...prev, key];
    if (key === 'configuracion') {
      // Marcar Configuración habilita todas sus pestañas; desmarcarla las quita
      next = next.filter(k => !CLAVES_TABS_CONFIGURACION.includes(k));
      if (!quitar) next = [...next, ...CLAVES_TABS_CONFIGURACION];
    } else if (CLAVES_TABS_CONFIGURACION.includes(key)) {
      // Sin ninguna pestaña, Configuración no tiene sentido: se desmarca sola
      const hayTabs = CLAVES_TABS_CONFIGURACION.some(k => next.includes(k));
      if (hayTabs && !next.includes('configuracion')) next = [...next, 'configuracion'];
      if (!hayTabs) next = next.filter(k => k !== 'configuracion');
    }
    return next;
  });

  const handleSave = async () => {
    setSaving(true);
    await onSave(cuenta?.user_cuenta_id, personalizado ? seleccionadas : null);
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Pantallas del Sidebar</h2>
          <button onClick={onClose} className="modal-close-btn">×</button>
        </div>
        <div className="modal-body">
          <p className="modal-subtitle">Usuario: <strong>{usuario.full_name}</strong></p>

          <label className="checkbox-item" style={{ marginBottom: '0.75rem' }}>
            <input
              type="checkbox"
              checked={personalizado}
              onChange={(e) => setPersonalizado(e.target.checked)}
            />
            <span>Personalizar pantallas</span>
          </label>
          <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '0.75rem' }}>
            {personalizado
              ? <>El usuario <strong>solo verá</strong> las pantallas marcadas. Los superusuarios siempre ven todas.</>
              : <>Sin personalizar, el usuario solo ve Calendario. El rol no define las pantallas.</>}
          </p>

          {personalizado && PANTALLAS_SIDEBAR.map(sec => (
            <div key={sec.seccion} style={{ marginBottom: '0.75rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '0.35rem' }}>
                {sec.seccion}
              </div>
              <div className="checkbox-list">
                {sec.items.map(it => (
                  <React.Fragment key={it.key}>
                    <label className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={seleccionadas.includes(it.key)}
                        onChange={() => toggle(it.key)}
                      />
                      <span>{it.label}</span>
                    </label>
                    {it.hijos && seleccionadas.includes(it.key) && it.hijos.map(h => (
                      <label key={h.key} className="checkbox-item" style={{ marginLeft: '1.5rem' }}>
                        <input
                          type="checkbox"
                          checked={seleccionadas.includes(h.key)}
                          onChange={() => toggle(h.key)}
                        />
                        <span>{h.label}</span>
                      </label>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="modal-footer">
          <button onClick={onClose} disabled={saving} className="estado-btn">Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary">
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal simple para editar rol
const RolModal = ({ usuario, cuenta, tiposUsuario, onClose, onSave }) => {
  const [rolSeleccionado, setRolSeleccionado] = useState(cuenta?.tipo_usuario?.id || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave(usuario.id, rolSeleccionado ? parseInt(rolSeleccionado) : null, cuenta?.user_cuenta_id);
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Editar Rol</h2>
          <button onClick={onClose} className="modal-close-btn">×</button>
        </div>
        <div className="modal-body">
          <p className="modal-subtitle">Usuario: <strong>{usuario.full_name}</strong></p>
          <select
            value={rolSeleccionado}
            onChange={(e) => setRolSeleccionado(e.target.value)}
            className="form-select"
          >
            <option value="">Sin rol</option>
            {tiposUsuario.map(tipo => (
              <option key={tipo.id} value={tipo.id}>{tipo.nombre}</option>
            ))}
          </select>
        </div>
        <div className="modal-footer">
          <button onClick={onClose} disabled={saving} className="estado-btn">Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary">
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal simple para editar permisos/grupos
const PermisosModal = ({ usuario, cuenta, grupos, onClose, onSave }) => {
  const gruposActuales = cuenta?.grupos || [];
  const [gruposSeleccionados, setGruposSeleccionados] = useState(gruposActuales.map(g => g.id));
  const [saving, setSaving] = useState(false);

  const handleToggle = (grupoId) => {
    setGruposSeleccionados(prev =>
      prev.includes(grupoId) ? prev.filter(id => id !== grupoId) : [...prev, grupoId]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(usuario.id, gruposSeleccionados, cuenta?.user_cuenta_id);
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Editar Grupos</h2>
          <button onClick={onClose} className="modal-close-btn">×</button>
        </div>
        <div className="modal-body">
          <p className="modal-subtitle">Usuario: <strong>{usuario.full_name}</strong></p>
          <div className="checkbox-list">
            {grupos.map(grupo => (
              <label key={grupo.id} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={gruposSeleccionados.includes(grupo.id)}
                  onChange={() => handleToggle(grupo.id)}
                />
                <span>{grupo.name}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button onClick={onClose} disabled={saving} className="estado-btn">Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary">
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

const EtiquetasModal = ({ usuario, cuenta, etiquetasDisponibles, onClose, onSave }) => {
  const etiquetasActuales = usuario.etiquetas_permitidas || [];
  const [seleccionadas, setSeleccionadas] = useState([...etiquetasActuales]);
  const [nuevaEtiqueta, setNuevaEtiqueta] = useState('');
  const [saving, setSaving] = useState(false);

  const handleToggle = (et) => {
    setSeleccionadas(prev =>
      prev.includes(et) ? prev.filter(e => e !== et) : [...prev, et]
    );
  };

  const handleAgregar = () => {
    const trimmed = nuevaEtiqueta.trim();
    if (trimmed && !seleccionadas.includes(trimmed)) {
      setSeleccionadas(prev => [...prev, trimmed]);
    }
    setNuevaEtiqueta('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleAgregar(); }
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(usuario.id, seleccionadas, cuenta?.user_cuenta_id);
    setSaving(false);
  };

  // Todas las etiquetas visibles: las disponibles + las ya seleccionadas que no estén en la lista
  const todasEtiquetas = [...new Set([...etiquetasDisponibles, ...seleccionadas])];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Etiquetas de Acceso</h2>
          <button onClick={onClose} className="modal-close-btn">×</button>
        </div>
        <div className="modal-body">
          <p className="modal-subtitle">
            Usuario: <strong>{usuario.full_name}</strong>
          </p>
          <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '0.75rem' }}>
            Si asignas etiquetas, el usuario <strong>solo verá</strong> los posts con esas etiquetas. Sin etiquetas asignadas, verá todo.
          </p>

          {/* Agregar etiqueta personalizada */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <input
              type="text"
              value={nuevaEtiqueta}
              onChange={(e) => setNuevaEtiqueta(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Escribir nueva etiqueta..."
              maxLength={50}
              style={{ flex: 1, padding: '0.4rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.85rem' }}
            />
            <button
              type="button"
              onClick={handleAgregar}
              disabled={!nuevaEtiqueta.trim()}
              className="btn-modal-add"
            >
              + Agregar
            </button>
          </div>

          {/* Lista de etiquetas disponibles */}
          {todasEtiquetas.length > 0 ? (
            <div className="checkbox-list">
              {todasEtiquetas.map((et, i) => (
                <label key={i} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={seleccionadas.includes(et)}
                    onChange={() => handleToggle(et)}
                  />
                  <span>🏷️ {et}</span>
                </label>
              ))}
            </div>
          ) : (
            <p style={{ fontSize: '0.82rem', color: '#9ca3af', textAlign: 'center', padding: '1rem 0' }}>
              Aún no hay etiquetas creadas en posts. Escribe una arriba para agregarla.
            </p>
          )}
        </div>
        <div className="modal-footer">
          <button onClick={onClose} disabled={saving} className="estado-btn">Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary">
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal para cambiar contraseña (solo superuser)
const CambiarPasswordModal = ({ usuario, onClose }) => {
  const [nuevaPassword, setNuevaPassword] = useState('');
  const [confirmarPassword, setConfirmarPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setError('');
    if (!nuevaPassword) return setError('Ingresa la nueva contraseña');
    if (nuevaPassword.length < 8) return setError('La contraseña debe tener al menos 8 caracteres');
    if (nuevaPassword !== confirmarPassword) return setError('Las contraseñas no coinciden');

    setSaving(true);
    try {
      const res = await usuariosService.cambiarPassword(usuario.id, nuevaPassword, confirmarPassword);
      toast.success(res.message || 'Contraseña actualizada');
      onClose();
    } catch (err) {
      setError(err.error || 'Error al cambiar la contraseña');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Cambiar Contraseña</h2>
          <button onClick={onClose} className="modal-close-btn">×</button>
        </div>
        <div className="modal-body">
          <p className="modal-subtitle">Usuario: <strong>{usuario.full_name || usuario.username}</strong></p>
          <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '1rem' }}>{usuario.email}</p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '0.3rem' }}>
                Nueva contraseña
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showNew ? 'text' : 'password'}
                  value={nuevaPassword}
                  onChange={(e) => setNuevaPassword(e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                  className="form-select"
                  style={{ paddingRight: '2.5rem' }}
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  style={{ position: 'absolute', right: '0.6rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '0.85rem' }}
                >
                  {showNew ? '🙈' : '👁'}
                </button>
              </div>
            </div>

            <div>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '0.3rem' }}>
                Confirmar contraseña
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmarPassword}
                  onChange={(e) => setConfirmarPassword(e.target.value)}
                  placeholder="Repite la contraseña"
                  className="form-select"
                  style={{ paddingRight: '2.5rem' }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  style={{ position: 'absolute', right: '0.6rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '0.85rem' }}
                >
                  {showConfirm ? '🙈' : '👁'}
                </button>
              </div>
            </div>

            {error && (
              <p style={{ color: '#ef4444', fontSize: '0.82rem', margin: 0 }}>
⚠ {error}
              </p>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button onClick={onClose} disabled={saving} className="estado-btn">Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary">
            {saving ? 'Guardando...' : 'Cambiar contraseña'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GestionUsuarios;
